// Selector de credencial para el step api_call.
// El executor (api_call_step_executor.py) busca `credential_id` y, si la URL del
// step es relativa, la une al `base_url` de la credencial. Aquí se elige la
// credencial (tipo "api") y se muestra la URL final que se llamará en runtime.
import React, { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import StyledSelect from "../ui/StyledSelect";
import credentialService from "../../services/credential.service";
import { ApiConnectionDialog } from "../api-connections/api-connection-dialog";
import { buildApiCredentialPayload } from "../api-connections/apiCredentialForm";
import { resolveApiCallUrl } from "./apiCallStep";

const ApiCredentialPicker = ({ credentialId, url, queryParams = null, onCredentialChange, compact = false }) => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [baseUrl, setBaseUrl] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    credentialService.listApis().then((res) => {
      if (cancelled) return;
      if (res.success) {
        setCredentials(res.data);
        setListError(null);
      } else {
        setListError(res.error?.message || "Could not load credentials");
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setBaseUrl(null);
    if (!credentialId) {
      setLoadingDetail(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingDetail(true);
    credentialService.getCredential("api", credentialId).then((res) => {
      if (cancelled) return;
      setBaseUrl(res.success ? res.data?.base_url || null : null);
      setLoadingDetail(false);
    });
    return () => { cancelled = true; };
  }, [credentialId]);

  // Alta sin salir del step: mismo diálogo que Settings → API Connections; la
  // conexión creada queda en la lista y seleccionada.
  const handleCreate = async (form) => {
    setSaving(true);
    setCreateError(null);
    const res = await credentialService.createCredential(buildApiCredentialPayload(form));
    setSaving(false);
    if (!res.success || !res.data?.id) {
      setCreateError(res.error?.message || "Could not create the connection");
      return;
    }
    setCredentials((prev) => [...prev, { id: res.data.id, name: res.data.name }]);
    setCreating(false);
    onCredentialChange(res.data.id);
  };

  const options = [
    { value: "", label: "None — URL is absolute" },
    ...credentials.map((c) => ({ value: c.id, label: c.name || c.id })),
  ];
  // Una credencial ya guardada en el step que no aparece en la lista (otra
  // cuenta, borrada, sin permisos) se sigue mostrando para no perderla al guardar.
  if (credentialId && !credentials.some((c) => c.id === credentialId)) {
    options.push({ value: credentialId, label: `${credentialId} (not in this account)` });
  }

  const labelClass = compact
    ? "block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2"
    : "block text-sm font-semibold text-white mb-2";
  const hintClass = compact ? "text-[10px] text-gray-400 mt-1" : "text-xs text-gray-400 mt-1";
  const monoClass = compact ? "text-xs text-white font-mono break-all" : "text-sm text-white font-mono break-all";

  return (
    <div className="space-y-2">
      <label className={labelClass}>Credential</label>
      {loading ? (
        <div className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-gray-400 flex items-center gap-2 text-xs">
          <Loader2 className="w-3 h-3 animate-spin" />
          Loading credentials...
        </div>
      ) : (
        <StyledSelect
          value={credentialId || ""}
          placeholder="Select credential..."
          onChange={(v) => onCredentialChange(v || null)}
          options={options}
        />
      )}
      {listError && <div className="text-[10px] text-red-400">{listError}</div>}
      {!loading && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-gray-500">
            {credentials.length === 0 && !listError ? "No API connections yet." : "Also managed in Settings → Credentials."}
          </span>
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-violet-300 hover:text-violet-200 shrink-0"
            onClick={() => { setCreateError(null); setCreating(true); }}
          >
            <Plus className="w-3 h-3" />
            New connection
          </button>
        </div>
      )}

      {credentialId && (
        <div className="px-3 py-2 bg-violet-500/10 border border-violet-500/30 rounded-lg">
          <div className="text-[10px] font-bold text-violet-300 uppercase tracking-wider mb-1">
            Base URL
          </div>
          <div className={monoClass}>
            {loadingDetail ? "Loading..." : baseUrl || "(unknown — credential could not be read)"}
          </div>
          {baseUrl && (
            <>
              <div className="text-[10px] font-bold text-violet-300 uppercase tracking-wider mt-2 mb-1">
                Final URL
              </div>
              <div className={monoClass}>{resolveApiCallUrl(baseUrl, url, queryParams)}</div>
            </>
          )}
          {!url && (
            <div className="text-[10px] text-amber-400 mt-1">No path yet — the call will hit the base URL.</div>
          )}
          <div className={hintClass}>
            The URL below is a path appended to this credential's base_url at runtime.
          </div>
        </div>
      )}

      <ApiConnectionDialog
        open={creating}
        onOpenChange={setCreating}
        connection={null}
        onSave={handleCreate}
        saving={saving}
        saveError={createError}
      />
    </div>
  );
};

export default ApiCredentialPicker;
